import React from 'react';
import { Box, alpha, styled } from '@mui/material';
import { format } from 'date-fns';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import type { GetStoreItemsSub } from 'src/openapi';
import { firstChargeDate } from 'src/helpers/subscription';
import { useAppDispatch, useAppSelector } from 'src/redux';
import {
  type OfferMoment,
  getCartDetails,
  getStoreItems,
} from 'src/redux/actions/store';
import { PriceMain, PriceRow, PriceTerm } from './sheet.styles';

/** The offer fields the server adds to the Pro item (read off it until the
 * generated client carries them) and the deadline getStoreItems derives. */
type OfferFields = {
  offer_ends_at?: string | null;
  offer_pending?: boolean;
  offer_moment?: OfferMoment | null;
  offer_moments?: OfferMoment[] | null;
  offer_announce?: 'gift' | 'pitch' | null;
  offer_window_no?: number | null;
  offer_deadline_ms?: number;
};
const offerOf = (item?: GetStoreItemsSub | null) => (item ?? {}) as OfferFields;

/**
 * When a time-limited Pro offer ends, on this device's clock, if the server
 * priced this item with one (the offer experiment's 24-hour window). Taken
 * from the seconds the server said were left when the price arrived, so a
 * phone whose clock is off still counts the real time.
 */
export const offerEndsAt = (item?: GetStoreItemsSub | null): Date | null => {
  const { offer_ends_at: endsAt, offer_deadline_ms: deadline } = offerOf(item);
  if (!endsAt) return null;
  const ends = new Date(deadline ?? endsAt);
  return Number.isNaN(ends.getTime()) ? null : ends;
};

/**
 * The item as it may be shown now: a "today only" free month is not offered
 * past its end, even for the moment before the re-read brings the price the
 * server now charges -- or if that re-read fails. Checkout refuses it anyway.
 */
export const useLiveOffer = <T extends GetStoreItemsSub>(
  item?: T | null,
): T | undefined => {
  const deadline = React.useMemo(() => offerEndsAt(item)?.getTime(), [item]);
  // The deadline that has passed while mounted (one already past at mount
  // passes at once).
  const [passed, setPassed] = React.useState<number>();
  React.useEffect(() => {
    if (deadline === undefined) return undefined;
    const t = window.setTimeout(
      () => setPassed(deadline),
      Math.max(0, deadline - Date.now()),
    );
    return () => window.clearTimeout(t);
  }, [deadline]);
  const ended = deadline !== undefined && passed === deadline;
  return React.useMemo(
    () =>
      ended && item
        ? { ...item, subscription_free_cycles: 0, subscription_free_days: null }
        : (item ?? undefined),
    [ended, item],
  );
};

/** The open window's identity (its end, as the server sent it), or null. */
export const offerWindowKey = (item?: GetStoreItemsSub | null) =>
  offerOf(item).offer_ends_at ?? null;

/** The treatment's offer fields on the Pro item: which moment opens its
 * window (and every one that may: an older server names only the one), how a
 * window's first screen announces it, and which window it is. */
export const offerTreatment = (item?: GetStoreItemsSub | null) => {
  const fields = offerOf(item);
  const moment = fields.offer_moment ?? null;
  return {
    moment,
    moments: fields.offer_moments?.length
      ? fields.offer_moments
      : [moment].filter((m): m is OfferMoment => !!m),
    announce: fields.offer_announce ?? null,
    windowNo: fields.offer_window_no ?? null,
    pending: !!fields.offer_pending,
  };
};

/**
 * One re-read of the store's prices per moment, across every countdown on the
 * page: several Pro surfaces share one item, and each would otherwise fetch
 * it when its window opens or ends. Keyed, so the opening read and the
 * closing one never hold each other up.
 */
const rereadAt: Record<string, number> = {};
const rereadOnce = (key: string, reread: () => void) => {
  if (Date.now() - (rereadAt[key] ?? 0) < 10_000) return;
  rereadAt[key] = Date.now();
  reread();
};

/**
 * When the server says this buyer's offer window is due (`offer_pending`) and
 * Pro is really on screen here, re-read the price as an on-screen read, which
 * opens it now -- while they are looking, never on a page that merely read
 * the prices.
 */
export const useOpenDueOffer = (
  item: GetStoreItemsSub | null | undefined,
  active: boolean,
  moment?: OfferMoment,
) => {
  const dispatch = useAppDispatch();
  const { pending, moments } = offerTreatment(item);
  // An offer that opens only at its moments (back from an apply, a return
  // visit) is left for those moments' screens: any other surface would ask
  // for nothing.
  const mine = !moments.length || (!!moment && moments.includes(moment));
  React.useEffect(() => {
    if (pending && active && mine) {
      rereadOnce(`open:${moment ?? ''}`, () =>
        dispatch(getStoreItems({ openOffer: true, offerMoment: moment })),
      );
    }
  }, [pending, active, mine, moment, dispatch]);
};

/** How long after zero to re-read: past the server's own end, so it answers
 * "shut" -- and again if it still said "open", until it does. */
const EXPIRY_REREAD_MS = 1500;
/** Past the re-read throttle, so each retry really fetches. */
const EXPIRY_RETRY_MS = 15_000;

const pad = (n: number) => String(n).padStart(2, '0');

/** "23:59:12" left until `ends`, ticking each second; null once it is over. */
const useTimeLeft = (ends: Date | null) => {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!ends) return undefined;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [ends]);
  if (!ends) return null;
  const left = Math.max(0, Math.floor((ends.getTime() - now) / 1000));
  if (!left) return null;
  const h = Math.floor(left / 3600);
  const m = Math.floor((left % 3600) / 60);
  return `${pad(h)}:${pad(m)}:${pad(left % 60)}`;
};

const Pill = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '3px 9px',
  borderRadius: 999,
  backgroundColor: alpha(theme.palette.error.main, 0.1),
  color: theme.palette.error.dark,
  fontSize: 11.5,
  fontWeight: 800,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  '& svg': { fontSize: 14 },
  ...theme.applyStyles('dark', { color: theme.palette.error.light }),
}));

/**
 * The offer's real end, counting down -- mounted on every surface that shows
 * Pro's price, whether or not an offer is running:
 * - Pro is on screen, so when the server says the buyer's clock would move
 *   (`offer_pending`), the price is re-read as an on-screen read and a due
 *   window opens now, while they are looking -- never on a page that merely
 *   read the prices.
 * - At zero the price is re-read, so every surface goes back to the list
 *   price the server now charges: the countdown never outlives the discount.
 */
const OfferCountdown = ({
  item,
  label = 'Offer ends in',
  active = true,
  inline = false,
}: {
  item?: GetStoreItemsSub | null;
  label?: string;
  /** Plain text in the colour around it, for a label that already says what
   * ends -- instead of the red pill that stands on its own. */
  inline?: boolean;
  /** Whether Pro is really in view here -- a surface that can sit below the
   * fold passes whether it has been seen, so a window is never opened unseen. */
  active?: boolean;
}) => {
  const dispatch = useAppDispatch();
  // A cart already loaded (a checkout summary on screen) is re-read with the
  // price when the offer ends, so its total says what Pay now charges.
  const hasCart = useAppSelector((s) => !!s.store.cartDetails?.id);
  useOpenDueOffer(item, active);
  const ends = React.useMemo(() => offerEndsAt(item), [item]);
  const left = useTimeLeft(ends);
  const expired = !!ends && !left;
  // Keyed on this item's deadline, not just "expired": a re-read that came
  // back still open (the server's end a moment later) has a new deadline, so
  // it is re-read again, until the price on screen is the one charged.
  const deadline = ends?.getTime();
  React.useEffect(() => {
    if (!expired) return undefined;
    const reread = () =>
      rereadOnce(`end:${deadline}`, () => {
        dispatch(getStoreItems());
        if (hasCart) dispatch(getCartDetails());
      });
    // Again every so often while this item still says it is on: a re-read
    // that failed is retried rather than left standing.
    let retry: number | undefined;
    const t = window.setTimeout(() => {
      reread();
      retry = window.setInterval(reread, EXPIRY_RETRY_MS);
    }, EXPIRY_REREAD_MS);
    return () => {
      window.clearTimeout(t);
      window.clearInterval(retry);
    };
  }, [expired, deadline, hasCart, dispatch]);
  if (!left) return null;
  if (inline) {
    return (
      <Box
        component="span"
        role="timer"
        aria-label={`${label} ${left}`}
        sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
      >
        {label} {left}
      </Box>
    );
  }
  return (
    <Pill role="timer" aria-label={`${label} ${left}`}>
      <TimerOutlinedIcon />
      {label} {left}
    </Pill>
  );
};

export default OfferCountdown;

/** Read out, not shown: the struck list price is "was ₹998" to a screen reader. */
const srOnly = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

/** The free month's label, with its countdown: the one place "today" is said. */
const FreeMonthChip = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 6,
  padding: '4px 10px',
  marginBottom: 8,
  borderRadius: 999,
  backgroundColor: alpha(theme.palette.success.main, 0.12),
  color: theme.palette.success.dark,
  fontSize: 11.5,
  fontWeight: 800,
  letterSpacing: '0.02em',
  '& svg': { fontSize: 15 },
  '& .dot': { opacity: 0.6 },
  ...theme.applyStyles('dark', { color: theme.palette.success.light }),
}));

/** What follows the free month, in one quiet line: from when, how much, and
 * the cut from the list price -- legible, never louder than ₹0 or Go Pro. */
const ThenLine = styled(PriceTerm)(({ theme }) => ({
  fontSize: 13,
  lineHeight: 1.45,
  '& b': { fontWeight: 700, color: theme.palette.text.primary },
  '& s': { color: theme.palette.text.secondary },
  '& .save': {
    fontWeight: 700,
    color: theme.palette.success.dark,
    whiteSpace: 'nowrap',
  },
  ...theme.applyStyles('dark', {
    '& .save': { color: theme.palette.success.light },
  }),
}));

/** When the first charge lands if bought now: after the free period, as the
 * server's mandate starts it (purchase time plus the free cycles or days). */
export const firstChargeOn = (freeCycles: number, freeDays?: number | null) =>
  format(firstChargeDate(freeCycles, freeDays), 'd MMM');

/**
 * The price block while a "today only" free first month is on. Two tiers:
 * ₹0 today, big, under a label that says what is free and when it ends; then
 * one quiet line with when the first charge comes and what it is.
 */
export const TodayOnlyPrice = ({
  item,
  freeCycles,
  freeDays,
  price,
  original,
  discountPct,
  isRecurring,
}: {
  item?: GetStoreItemsSub | null;
  freeCycles: number;
  freeDays?: number | null;
  price: number;
  original: number;
  discountPct: number;
  isRecurring: boolean;
}) => {
  if (!offerEndsAt(item)) return null;
  const span = freeDays
    ? `First ${freeDays} days`
    : freeCycles === 1
      ? 'First month'
      : `First ${freeCycles} months`;
  return (
    <Box sx={{ mb: 1.75 }}>
      <FreeMonthChip>
        <CardGiftcardRoundedIcon />
        <span>{span.toUpperCase()} FREE</span>
        <span className="dot">·</span>
        <OfferCountdown item={item} label="ends in" inline />
      </FreeMonthChip>
      <PriceRow sx={{ mb: 0.75 }}>
        <PriceMain sx={{ fontSize: 34 }}>₹0</PriceMain>
        <PriceTerm sx={{ fontSize: 14, fontWeight: 700 }}>today</PriceTerm>
      </PriceRow>
      <ThenLine>
        Then{' '}
        <b>
          ₹{price}
          {isRecurring ? '/month' : ''}
        </b>{' '}
        from {firstChargeOn(freeCycles, freeDays)}
        {original > price && (
          <>
            {' · '}
            <Box component="span" sx={srOnly}>
              was{' '}
            </Box>
            <s>₹{original}</s>
          </>
        )}
        {discountPct > 0 && <span className="save"> {discountPct}% off</span>}
      </ThenLine>
    </Box>
  );
};
